import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Warehouses() {
  const { can } = useAuth();
  const CAT_LABEL = { produk_jadi: "Produk Jadi", material: "Material", umum: "Umum" };
  const CAT_CLS = { produk_jadi: "bg-blue-50 text-blue-700 border-blue-200", material: "bg-amber-50 text-amber-700 border-amber-200", umum: "bg-taupe/15 text-cocoa border-taupe/30" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/warehouses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setOpen(true); };
  const openEdit = (w) => { setEdit(w); setOpen(true); };
  const remove = async (w) => {
    if (!window.confirm(`Hapus gudang "${w.name}"? Tindakan ini tidak dapat dibatalkan.`)) return;
    try { await api.delete(`/warehouses/${w.id}`); toast.success("Gudang dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="warehouses-page">
      <PageHeader title="Gudang & Rak" subtitle="Master gudang dan lokasi/rak">
        {can("warehouse_admin") && (
          <Button onClick={openNew} data-testid="add-warehouse-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">
            <Plus className="w-4 h-4 mr-1.5" /> Gudang
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((w) => (
          <Panel key={w.id} className="p-5" >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold text-cocoa text-lg">{w.name}</div>
                <div className="text-xs font-mono text-wmuted">{w.code}</div>
                <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border ${CAT_CLS[w.category || "produk_jadi"] || "border-taupe/30 text-wmuted"}`} data-testid={`warehouse-cat-${w.id}`}>{CAT_LABEL[w.category || "produk_jadi"] || w.category}{w.is_sellable === false ? " · non-sellable" : ""}</span>
                {w.sales_bucket_tag && <span className="inline-block mt-1 ml-1 text-[11px] px-2 py-0.5 rounded-full border border-cocoa/30 text-cocoa font-semibold" data-testid={`warehouse-bucket-${w.id}`}>{w.sales_bucket_tag}</span>}
              </div>
              {can("warehouse_admin") && (
                <div className="flex items-center">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(w)} data-testid={`edit-warehouse-${w.id}`}>
                    <Pencil className="w-4 h-4 text-cocoa" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(w)} data-testid={`delete-warehouse-${w.id}`}>
                    <Trash2 className="w-4 h-4 text-status-reject" />
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-3 text-sm text-cocoa/80 space-y-1">
              <div><span className="text-wmuted">Alamat:</span> {w.address || "-"}</div>
              <div><span className="text-wmuted">PIC:</span> {w.pic || "-"}</div>
              <div><span className="text-wmuted">Status:</span> {w.active ? "Aktif" : "Nonaktif"}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(w.locations || []).map((l) => (
                <span key={l} className="text-xs bg-ivory border border-taupe/20 px-2 py-0.5 rounded text-cocoa">{l}</span>
              ))}
            </div>
          </Panel>
        ))}
      </div>

      <WarehouseDialog open={open} setOpen={setOpen} edit={edit} onDone={load} />
    </div>
  );
}

function WarehouseDialog({ open, setOpen, edit, onDone }) {
  const [form, setForm] = useState({});
  const [locs, setLocs] = useState("");
  useEffect(() => {
    if (open) {
      setForm(edit || { name: "", code: "", address: "", pic: "", active: true, is_sellable: true, notes: "", category: "produk_jadi" });
      setLocs((edit?.locations || []).join(", "));
    }
  }, [open, edit]);

  const save = async () => {
    const payload = { ...form, active: form.active !== false, locations: locs.split(",").map(s => s.trim()).filter(Boolean) };
    try {
      if (edit) await api.put(`/warehouses/${edit.id}`, payload);
      else await api.post("/warehouses", payload);
      toast.success("Gudang disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Gudang</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Gudang *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="warehouse-name-input" /></div>
          <div><Label>Kode Gudang *</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="warehouse-code-input" /></div>
          <div><Label>Kategori Gudang *</Label>
            <Select value={form.category || "produk_jadi"} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="warehouse-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="produk_jadi">Produk Jadi</SelectItem>
                <SelectItem value="material">Material (Kain & Aksesori)</SelectItem>
                <SelectItem value="umum">Umum (semua)</SelectItem>
                <SelectItem value="qc_hold">QC Hold</SelectItem>
                <SelectItem value="rework">Rework</SelectItem>
                <SelectItem value="defect">Defect</SelectItem>
                <SelectItem value="transit">Transit</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-wmuted mt-1">Gudang Material hanya untuk kain/aksesori. Produk Jadi untuk barang jadi hasil produksi, transfer & penjualan.</p>
          </div>
          <div><Label>Alamat</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Penanggung Jawab</Label><Input value={form.pic || ""} onChange={(e) => setForm({ ...form, pic: e.target.value })} /></div>
          <div><Label>Lokasi/Rak (pisah koma)</Label><Input value={locs} onChange={(e) => setLocs(e.target.value)} placeholder="A-01, A-02, B-01" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Aktif</Label></div>
          <div className="flex items-center gap-2"><Switch checked={form.is_sellable !== false} onCheckedChange={(v) => setForm({ ...form, is_sellable: v })} data-testid="warehouse-sellable-switch" /><Label>Is Sellable (boleh jadi sumber penjualan)</Label></div>
          <div className="mt-2 rounded-lg border border-taupe/25 p-3 space-y-2.5">
            <div className="text-xs font-semibold text-cocoa uppercase tracking-wide">Konfigurasi Stok Gudang</div>
            {[
              ["include_in_global_available_stock", "Include in All Available Stock", "Stok ikut dihitung sebagai stok global yang tersedia untuk operasional Sales."],
              ["allow_keep", "Allow Stock for Keep", "Stok gudang dapat digunakan untuk membuat Keep."],
              ["include_in_company_stock", "Include in Company Total Stock", "Stok tetap dihitung sebagai aset fisik perusahaan."],
              ["allow_sales", "Allow Sales", "Stok boleh digunakan untuk transaksi penjualan."],
              ["allow_stock_transfer", "Allow Stock Transfer", "Gudang dapat menjadi asal atau tujuan transfer."],
              ["allow_stock_opname", "Allow Stock Opname", "Gudang dapat melakukan stock opname."],
            ].map(([k, label, desc]) => (
              <div key={k} className="flex items-start gap-3">
                <Switch checked={form[k] !== false} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`warehouse-flag-${k}`} />
                <div><Label className="text-sm">{label}</Label><p className="text-[11px] text-wmuted leading-snug">{desc}</p></div>
              </div>
            ))}
          </div>
          <div>
            <Label>Sales Bucket (HOME / STORE)</Label>
            <Select value={form.sales_bucket_tag || "__none__"} onValueChange={(v) => setForm({ ...form, sales_bucket_tag: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="warehouse-bucket-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— (tidak masuk HOME/STORE)</SelectItem>
                <SelectItem value="HOME">HOME</SelectItem>
                <SelectItem value="STORE">STORE</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-wmuted mt-1">Menentukan gudang ini masuk hitungan Home/Store di Product. Beberapa gudang boleh sama-sama HOME atau STORE.</p>
          </div>
          <div><Label>Catatan</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="warehouse-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-warehouse-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
